import axios from "axios";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { Add, ArrowBack, Delete, Save } from "@mui/icons-material";
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import Layout from "../../../Components/Layout/Layout";
import Tile from "Components/Tiles/Tile";
import Sidebar from "Components/Sidebar/Sidebar";
import SidebarGroup from "Components/Sidebar/SidebarGroup";
import SidebarButton from "Components/Sidebar/SidebarButton";
import showMessage from "Dialogs/showMessage";
import ask from "Dialogs/ask";
import useCompanies, { getCompanyReference } from "../../../system/useCompanies";
import useCompounds from "../../../system/useCompounds";
import {
	TariffAxle, TariffBilling, TariffPrice, TariffSurcharge, TariffSurchargeType, TariffVehicleClass,
	TariffVolumeCategory, TransportTariff
} from "../../../system/Domain";
import {
	axleLabels, billingLabels, maximumNumberOfVehicles, surchargeLabels, vehicleClassLabels,
	volumeCategoryLabels
} from "./tariffLabels";

type Line = Omit<TariffPrice, "pricePerVehicle"> & { pricePerVehicle: number | "" };
type Form = Omit<TransportTariff, "prices" | "surcharges"> & { prices: Line[]; surcharges: TariffSurcharge[] };

const noCompound = "";
const dateOnly = (value?: string | null) => (value ? moment(value).format("YYYY-MM-DD") : "");

const createNew = (carrier?: { id: number; name: string }): Form => ({
	id: "",
	carrier: carrier as any,
	axle: "OwnAxle",
	billing: "PerVehicle",
	originCompound: null,
	destinationCompound: null,
	validFrom: moment().format("YYYY-MM-DD"),
	validTo: null,
	flatFee: null,
	dieselSurchargeApplies: false,
	surcharges: [],
	prices: []
} as unknown as Form);

const quantities = Array.from({ length: maximumNumberOfVehicles }, (_, i) => i + 1);

// Fields of a price line that do not apply to the current billing or route are cleared.
const normalizeLine = (line: Line, form: Form): Line => ({
	...line,
	originArea: form.originCompound ? null : line.originArea ?? "",
	destinationArea: form.destinationCompound ? null : line.destinationArea ?? "",
	quantity: form.billing === "ByQuantity" ? line.quantity ?? 1 : null,
	vehicleClass: form.billing === "ByVehicleClass" ? line.vehicleClass ?? "Pkw" : null,
	volumeCategory: form.billing === "ByVolumeCategory" ? line.volumeCategory ?? "S" : null
});

export default () => {
	const history = useHistory();
	const { id, companyId } = useParams<{ id?: string; companyId: string }>();
	const [companies] = useCompanies();
	const [compounds] = useCompounds({ includeAmazonCompounds: true });
	const [form, setForm] = useState<Form | undefined>(id ? undefined : createNew());
	const [areaCodes, setAreaCodes] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		(async () => {
			setAreaCodes((await axios.get<string[]>("/api/transport-tariffs/area-codes")).data);

			if (id) {
				const { data } = await axios.get<Form>(`/api/transport-tariffs/${id}`);
				setForm(data);
			}
		})();
	}, [id]);

	// The carrier is given by the company page the tariff belongs to.
	useEffect(() => {
		const company = companies?.find(c => c.id === Number(companyId));
		if (!id && company && form && !form.carrier) {
			setForm({ ...form, carrier: getCompanyReference(company) });
		}
	}, [companies, companyId, id, form]);

	const carriers = useMemo(
		() => [...(companies ?? [])]
			.filter(c => c.transports?.active || c.name === form?.carrier?.name)
			.sort((a, b) => a.name.localeCompare(b.name)),
		[companies, form?.carrier?.name]
	);
	const compoundNames = useMemo(() => [...(compounds ?? [])].map(c => c.name).sort(), [compounds]);

	if (!form) {
		return <Typography align="center" component="div"><CircularProgress /></Typography>;
	}

	const hasOriginArea = !form.originCompound;
	const hasDestinationArea = !form.destinationCompound;

	const update = (changes: Partial<Form>) =>
		setForm(f => {
			const next = { ...f!, ...changes };
			return { ...next, prices: next.prices.map(l => normalizeLine(l, next)) };
		});

	// Changing billing or route side re-interprets all price lines, so existing lines are only reset after confirmation.
	const updateStructure = async (changes: Partial<Form>) => {
		if (form.prices.length > 0) {
			const { yes } = await ask("Die Preiszeilen passen nicht mehr zur Änderung und werden zurückgesetzt. Fortfahren?");
			if (!yes) {
				return;
			}
		}

		update({ ...changes, prices: [] });
	};

	const updateLine = (index: number, changes: Partial<Line>) =>
		update({ prices: form.prices.map((l, i) => (i === index ? { ...l, ...changes } : l)) });

	const updateSurcharge = (type: TariffSurchargeType, amount: string) => {
		const others = form.surcharges.filter(s => s.type !== type);
		update({ surcharges: amount === "" ? others : [...others, { type, amount: Number(amount) }] });
	};

	const getSurcharge = (type: TariffSurchargeType) =>
		form.surcharges.find(s => s.type === type)?.amount ?? "";

	const addLine = () => update({ prices: [...form.prices, normalizeLine({ pricePerVehicle: "" }, form)] });

	// Creates empty lines for all postal areas, prices that stay empty are dropped when saving.
	const generateLines = () => {
		const areaSide: "originArea" | "destinationArea" | undefined = !form.originCompound
			? "originArea"
			: !form.destinationCompound ? "destinationArea" : undefined;
		if (!areaSide) {
			return;
		}

		const variants: Partial<Line>[] = form.billing === "PerVehicle"
			? [{}]
			: form.billing === "ByQuantity"
				? quantities.map(quantity => ({ quantity }))
				: form.billing === "ByVehicleClass"
					? (Object.keys(vehicleClassLabels) as TariffVehicleClass[]).map(vehicleClass => ({ vehicleClass }))
					: (Object.keys(volumeCategoryLabels) as TariffVolumeCategory[]).map(volumeCategory => ({ volumeCategory }));

		const existing = new Set(form.prices.map(l => JSON.stringify([l.originArea, l.destinationArea, l.quantity, l.vehicleClass, l.volumeCategory])));
		const generated = areaCodes
			.flatMap(area => variants.map(v => normalizeLine({ ...v, [areaSide]: area, pricePerVehicle: "" }, form)))
			.filter(l => !existing.has(JSON.stringify([l.originArea, l.destinationArea, l.quantity, l.vehicleClass, l.volumeCategory])));

		update({ prices: [...form.prices, ...generated] });
	};

	const save = async () => {
		const filled = form.prices.filter(l => l.pricePerVehicle !== "");
		if (filled.some(l => (hasOriginArea && !l.originArea) || (hasDestinationArea && !l.destinationArea))) {
			await showMessage({ title: "Fehler", message: "Bitte bei allen Preiszeilen das PLZ-Gebiet auswählen." });
			return;
		}

		setIsSaving(true);
		try {
			const body = {
				...form,
				validTo: form.validTo || null,
				flatFee: form.flatFee === null || (form.flatFee as unknown) === "" ? null : Number(form.flatFee),
				prices: form.prices
					.filter(l => l.pricePerVehicle !== "")
					.map(l => ({ ...l, pricePerVehicle: Number(l.pricePerVehicle) }))
			};

			const { data } = id
				? await axios.put<Form>(`/api/transport-tariffs/${id}`, body)
				: await axios.post<Form>(`/api/transport-tariffs`, body);

			await showMessage({ title: "Informationen", message: "Tarif gespeichert." });

			if (id) {
				setForm(data);
			} else {
				history.replace(`/companies/${companyId}/tariffs/${data.id}`);
			}
		} finally {
			setIsSaving(false);
		}
	};

	const remove = async () => {
		const { yes } = await ask("Soll der Tarif wirklich gelöscht werden?");
		if (yes) {
			await axios.delete(`/api/transport-tariffs/${id}`);
			history.push(`/companies/${companyId}/tariffs`);
		}
	};

	return (
		<Layout
			title="Frachtführer-Tarife"
			search="disabled"
			sidebar={(
				<Sidebar>
					<SidebarGroup>
						<SidebarButton
							color="primary"
							startIcon={<Save />}
							label="Speichern"
							disabled={isSaving || !form.carrier}
							onClick={save}
						/>
					</SidebarGroup>
					{(
						<SidebarGroup>
							<SidebarButton
								startIcon={<ArrowBack />}
								label="Zu den Tarifen"
								onClick={() => history.push(`/companies/${companyId}/tariffs`)}
							/>
						</SidebarGroup>
					)}
					{id && (
						<SidebarGroup>
							<SidebarButton color="secondary" startIcon={<Delete />} label="Löschen" onClick={remove} />
						</SidebarGroup>
					)}
				</Sidebar>
			)}
		>
			<Box mt={2}>
				<Tile title="Tarif">
					<Grid container spacing={2}>
						<Grid item xs={12} md={4}>
							<TextField variant="standard"
								select fullWidth disabled label="Frachtführer"
								value={form.carrier?.name ?? ""}
								onChange={e => update({ carrier: getCompanyReference(carriers.find(c => c.name === e.target.value)!) })}
							>
								{carriers.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
							</TextField>
						</Grid>
						<Grid item xs={12} md={4}>
							<TextField variant="standard"
								select fullWidth label="Achse" value={form.axle}
								onChange={e => {
									const axle = e.target.value as TariffAxle;
									updateStructure({ axle, billing: axle === "OwnAxle" ? "PerVehicle" : "ByQuantity" });
								}}
							>
								{Object.entries(axleLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
							</TextField>
						</Grid>
						<Grid item xs={12} md={4}>
							<TextField variant="standard"
								select fullWidth label="Abrechnung" value={form.billing}
								disabled={form.axle === "OwnAxle"}
								helperText={form.axle === "OwnAxle" ? "Eigene Achse wird immer pro Fahrzeug abgerechnet" : undefined}
								onChange={e => updateStructure({ billing: e.target.value as TariffBilling })}
							>
								{Object.entries(billingLabels)
									.filter(([k]) => (form.axle === "OwnAxle") === (k === "PerVehicle"))
									.map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
							</TextField>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField variant="standard"
								select fullWidth label="Start-Compound" value={form.originCompound?.name ?? noCompound}
								helperText="Leer = PLZ-Gebiet je Preiszeile"
								onChange={e => updateStructure({ originCompound: e.target.value ? { name: e.target.value } : null })}
							>
								<MenuItem value={noCompound}>PLZ-Gebiet</MenuItem>
								{compoundNames.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
							</TextField>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField variant="standard"
								select fullWidth label="Ziel-Compound" value={form.destinationCompound?.name ?? noCompound}
								helperText="Leer = PLZ-Gebiet je Preiszeile"
								onChange={e => updateStructure({ destinationCompound: e.target.value ? { name: e.target.value } : null })}
							>
								<MenuItem value={noCompound}>PLZ-Gebiet</MenuItem>
								{compoundNames.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
							</TextField>
						</Grid>
						<Grid item xs={12} md={3}>
							<TextField variant="standard"
								fullWidth type="date" label="Gültig ab" InputLabelProps={{ shrink: true }}
								value={dateOnly(form.validFrom)}
								onChange={e => update({ validFrom: e.target.value })}
							/>
						</Grid>
						<Grid item xs={12} md={3}>
							<TextField variant="standard"
								fullWidth type="date" label="Gültig bis" InputLabelProps={{ shrink: true }}
								value={dateOnly(form.validTo)}
								onChange={e => update({ validTo: e.target.value || null })}
							/>
						</Grid>
						<Grid item xs={12} md={2}>
							<TextField variant="standard"
								fullWidth type="number" label="Pauschale je Auftrag (€)"
								value={form.flatFee ?? ""}
								onChange={e => update({ flatFee: e.target.value === "" ? null : Number(e.target.value) })}
							/>
						</Grid>
						<Grid item xs={12} md={2}>
							<TextField variant="standard"
								fullWidth type="number" label="Fehlfahrt (€)"
								value={getSurcharge("MissedTransport")}
								onChange={e => updateSurcharge("MissedTransport", e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} md={2}>
							<TextField variant="standard"
								fullWidth type="number" label="Kurzzeitkennzeichen (€)"
								value={getSurcharge("ShortTermPlate")}
								onChange={e => updateSurcharge("ShortTermPlate", e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<FormControlLabel
								control={(
									<Checkbox
										checked={!!form.dieselSurchargeApplies}
										onChange={e => update({ dieselSurchargeApplies: e.target.checked })}
									/>
								)}
								label="Dieselzuschlag fällt an"
							/>
						</Grid>
					</Grid>
				</Tile>
			</Box>
			<Box mt={2}>
				<Tile title={`Preise (${form.prices.length} Zeilen)`}>
					<Box mb={2} display="flex" gap={1}>
						<Button startIcon={<Add />} onClick={addLine}>Zeile hinzufügen</Button>
						{(hasOriginArea !== hasDestinationArea) && (
							<Button onClick={generateLines}>Zeilen für alle Gebiete erzeugen</Button>
						)}
					</Box>
					<Table size="small">
						<TableHead>
							<TableRow>
								{hasOriginArea && <TableCell>Von PLZ</TableCell>}
								{hasDestinationArea && <TableCell>Nach PLZ</TableCell>}
								{form.billing === "ByQuantity" && <TableCell>Anzahl</TableCell>}
								{form.billing === "ByVehicleClass" && <TableCell>Fahrzeugklasse</TableCell>}
								{form.billing === "ByVolumeCategory" && <TableCell>Volumen</TableCell>}
								<TableCell>Preis je Fahrzeug (€)</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{form.prices.map((line, index) => (
								<TableRow key={index}>
									{hasOriginArea && (
										<TableCell>
											<TextField variant="standard" select size="small" value={line.originArea ?? ""} onChange={e => updateLine(index, { originArea: e.target.value })}>
												{areaCodes.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
											</TextField>
										</TableCell>
									)}
									{hasDestinationArea && (
										<TableCell>
											<TextField variant="standard" select size="small" value={line.destinationArea ?? ""} onChange={e => updateLine(index, { destinationArea: e.target.value })}>
												{areaCodes.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
											</TextField>
										</TableCell>
									)}
									{form.billing === "ByQuantity" && (
										<TableCell>
											<TextField variant="standard" select size="small" value={line.quantity ?? 1} onChange={e => updateLine(index, { quantity: Number(e.target.value) })}>
												{quantities.map(q => <MenuItem key={q} value={q}>{q}</MenuItem>)}
											</TextField>
										</TableCell>
									)}
									{form.billing === "ByVehicleClass" && (
										<TableCell>
											<TextField variant="standard" select size="small" value={line.vehicleClass ?? "Pkw"} onChange={e => updateLine(index, { vehicleClass: e.target.value as TariffVehicleClass })}>
												{Object.entries(vehicleClassLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
											</TextField>
										</TableCell>
									)}
									{form.billing === "ByVolumeCategory" && (
										<TableCell>
											<TextField variant="standard" select size="small" value={line.volumeCategory ?? "S"} onChange={e => updateLine(index, { volumeCategory: e.target.value as TariffVolumeCategory })}>
												{Object.entries(volumeCategoryLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
											</TextField>
										</TableCell>
									)}
									<TableCell>
										<TextField variant="standard"
											type="number" size="small" value={line.pricePerVehicle}
											onChange={e => updateLine(index, { pricePerVehicle: e.target.value === "" ? "" : Number(e.target.value) })}
										/>
									</TableCell>
									<TableCell>
										<IconButton onClick={() => update({ prices: form.prices.filter((_, i) => i !== index) })}>
											<Delete />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Tile>
			</Box>
		</Layout>
	);
};
